import { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useAuth } from './state/AuthContext'
import { LoginPage } from './pages/LoginPage'
import { SignUpPage } from './pages/SignUpPage'
import { PrivateBetaWaitlistPage } from './pages/PrivateBetaWaitlistPage'
import { OAuthCallbackPage } from './pages/OAuthCallbackPage'
import { MagicLinkPage } from './pages/MagicLinkPage'
import { StartHostingPage } from './pages/StartHostingPage'
import { DashboardPage } from './pages/DashboardPage'
import { AiProducerPage } from './pages/AiProducerPage'
import { EventPage } from './pages/EventPage'
import { CanvasPage } from './pages/CanvasPage'
import { SponsorsPage } from './pages/SponsorsPage'
import { SponsorSpecsPage } from './pages/SponsorSpecsPage'
import { LandingPage } from './pages/LandingPage'
import StreamsMonitorPage from './pages/StreamsMonitorPage'
import { StreamInvitePage } from './pages/StreamInvitePage'
import { LiveEventPage } from './pages/LiveEventPage'
import { EventShortcodeRedirectPage } from './pages/EventShortcodeRedirectPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { InsightsPage } from './pages/InsightsPage'
import { InsightArticlePage } from './pages/InsightArticlePage'
import { FAQPage } from './pages/FAQPage'
import { McpDocsPage } from './pages/McpDocsPage'
import { ExplorePage } from './pages/ExplorePage'
import { HostWatchAlongPage } from './pages/HostWatchAlongPage'
import { InvitePage } from './pages/InvitePage'
import { ChatGptGuidePage } from '@/pages/ChatGptGuidePage'
import { OAuthAuthorizePage } from './pages/OAuthAuthorizePage'
import { AdminGuard } from '@/components/admin/AdminGuard'
import { PrivateRoute } from '@/components/auth/PrivateRoute'
import { DirectorRoute } from '@/components/auth/DirectorRoute'
import { isViewer, withoutOrganization } from '@/components/auth/isViewer'
import { AdminHomePage } from './pages/admin/AdminHomePage'
import { AdminTeamsPage } from './pages/admin/AdminTeamsPage'
import { AdminUsersPage } from './pages/admin/AdminUsersPage'
import { AdminPrivateBetaPage } from './pages/admin/AdminPrivateBetaPage'
import { AdminOrganizationsPage } from './pages/admin/AdminOrganizationsPage'
import { AdminOrganizationDetailPage } from './pages/admin/AdminOrganizationDetailPage'
import { AdminSrtBridgesPage } from './pages/admin/AdminSrtBridgesPage'
import { AboutPage } from './pages/AboutPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { TermsPage } from '@/pages/TermsPage'
import { WalkthroughPage } from './pages/WalkthroughPage'
import SettingsPage from './pages/SettingsPage'
import { EventSettingsPage } from './pages/EventSettingsPage'
import { FeedbackPage } from './pages/FeedbackPage'
import { FeedbackDetailPage } from './pages/FeedbackDetailPage'
import { FeedbackFloatingButton } from '@/components/feedback/FeedbackFloatingButton'
import { SiteHeader } from '@/components/SiteHeader'
import { SiteFooter } from '@/components/SiteFooter'
import { Toaster } from '@/components/play'

const DesignSitePage = lazy(() => import('./pages/design/DesignSitePage').then((module) => ({ default: module.DesignSitePage })))

/**
 * A retired viewer's link (`/v/<id>`, `/watch/<id>`, `/comrades/<id>`): the event's watch screen, every event's public
 * page. `/e/` finds the event by id or shortcode and settles on the shortcode.
 */
function ToWatchScreen() {
  const { eventId = '' } = useParams<{ eventId: string }>()
  const { search, hash } = useLocation()
  return <Navigate to={`/e/${encodeURIComponent(eventId)}${search}${hash}`} replace />
}

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [pathname])
  return null
}

export default function App() {
  const { isAuthenticated, authReady, user, organization } = useAuth()
  const location = useLocation()

  const isLiveEventPage = location.pathname.startsWith('/live/')
  const isWatchPage = location.pathname.startsWith('/watch/')
  const isComradesPage = location.pathname === '/comrades' || location.pathname.startsWith('/comrades/')
  const isComrades2026Page = location.pathname === '/comrades2026' || location.pathname.startsWith('/comrades2026/')
  const isShortcodeRedirectPage = location.pathname.startsWith('/e/')
  const isVTemplatePage = location.pathname.startsWith('/v/')
  const isDesignSite = location.pathname === '/design' || location.pathname.startsWith('/design/')
  // Pages that lay out their own shell (design/DESIGN.md, One shell): the entry pages, the site's and docs' pages
  // (SitePage: SiteHeader, main, SiteFooter), Events and the account page, a camera's invite, and the event workspace
  // (WorkspaceHeader) with its views, settings, sponsors and AI Producer canvas.
  const isPlaySurface = ['/', '/signup', '/login', '/oauth/callback', '/auth/magic-link', '/start-hosting', '/oauth/authorize', '/private-beta', '/app', '/dashboard', '/settings'].includes(location.pathname)
    || /^\/(?:docs\/(?:chatgpt|mcp)|explore|host|invite|about|faq|privacy|terms|sponsors|how-it-works|news|insights(?:\/[^/]+)?)\/?$/.test(location.pathname)
    || location.pathname.startsWith('/stream/') || /^\/events\/[^/]+(?:\/(?:map|multiview|athletes|team|settings|sponsors|canvas))?\/?$/.test(location.pathname)
  // Signed in with an organisation (or a superadmin), or managing someone else's event, once the account has loaded.
  // Feedback is an organisation's: viewers, and managers without one, get no feedback button.
  const host = isAuthenticated && authReady && !isViewer(organization, user)
  const feedback = host && !withoutOrganization(organization, user)

  // Full-screen pages without header/footer
  if (isLiveEventPage) {
    return (
      <>
        <Routes>
          <Route path="/live/:eventId" element={<LiveEventPage />} />
        </Routes>
        <Toaster />
      </>
    )
  }

  // The retired standalone and Comrades viewers: their links open the watch screen.
  if (isWatchPage || isComradesPage) {
    return (
      <Routes>
        <Route path="/watch/:eventId" element={<ToWatchScreen />} />
        <Route path="/comrades" element={<Navigate to="/e/comrades" replace />} />
        <Route path="/comrades/:eventId" element={<ToWatchScreen />} />
      </Routes>
    )
  }

  // Legacy Comrades 2026 link — keep it working, but make /comrades canonical.
  if (isComrades2026Page) {
    const canonicalComradesPath = location.pathname.replace(/^\/comrades2026(?=\/|$)/, '/comrades')
    return <Navigate to={`${canonicalComradesPath}${location.search}${location.hash}`} replace />
  }

  // The event's public page composes the site's header and footer itself (WatchScreenPage's WatchShell): a phone on its
  // side shows the video alone, and a phone's footer ends its Cameras panel.
  if (isShortcodeRedirectPage) {
    return (
      <>
        <Routes>
          {/* /e/:shortcode and deeper paths (a stage's /e/:shortcode/@handle, later) land on the same page. */}
          <Route path="/e/:shortcode/*" element={<EventShortcodeRedirectPage />} />
        </Routes>
        <Toaster />
      </>
    )
  }

  // Public Play design reference site — its own header and navigation.
  if (isDesignSite) {
    return (
      <Routes>
        <Route path="/design/*" element={<Suspense fallback={<div className="play-theme min-h-screen" />}><DesignSitePage /></Suspense>} />
      </Routes>
    )
  }

  // The retired template viewer's links open the watch screen; a camera's onboarding page stays at /v/<id>/onboarding.
  if (isVTemplatePage) {
    return (
      <>
        <Routes>
          <Route path="/v/:eventId" element={<ToWatchScreen />} />
          <Route path="/v/:eventId/onboarding" element={<OnboardingPage />} />
        </Routes>
        <Toaster />
      </>
    )
  }


  const routes = (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      {/* The intro reel sends people to streambridge.live/news (docs/features/news.md). */}
      <Route path="/news" element={<Navigate to="/insights" replace />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/insights/:slug" element={<InsightArticlePage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/host" element={<HostWatchAlongPage />} />
      {/* An invite link is /invite#<token>: the token stays in the fragment, never a path or query string. */}
      <Route path="/invite" element={<InvitePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/sponsors" element={<SponsorSpecsPage />} />
      <Route path="/how-it-works" element={<WalkthroughPage />} />
      <Route path="/docs/mcp" element={<McpDocsPage />} />
      <Route path="/docs/chatgpt" element={<ChatGptGuidePage />} />
      <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      {/* The sign-in email links here; services/streambridge/app/mailers/user_auth_mailer.rb must match. */}
      <Route path="/auth/magic-link" element={<MagicLinkPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/private-beta" element={<PrivateBetaWaitlistPage />} />
      <Route path="/stream/:streamKey" element={<StreamInvitePage />} />
      <Route path="/live/:eventId" element={<LiveEventPage />} />
      <Route path="/app" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/app/ai-producer" element={<DirectorRoute><AiProducerPage /></DirectorRoute>} />
      <Route path="/events/:id" element={<PrivateRoute><EventPage /></PrivateRoute>}>
        <Route index element={null} />
        <Route path="multiview" element={null} />
        <Route path="map" element={null} />
        <Route path="athletes" element={null} />
        <Route path="team" element={null} />
      </Route>
      <Route path="/events/:eventId/canvas" element={<DirectorRoute><CanvasPage /></DirectorRoute>} />
      <Route path="/events/:id/sponsors" element={<PrivateRoute><SponsorsPage /></PrivateRoute>} />
      <Route path="/events/:id/settings" element={<PrivateRoute><EventSettingsPage /></PrivateRoute>} />
      <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      {/* A viewer opening a host page lands here (PrivateRoute) to name an organisation. */}
      <Route path="/start-hosting" element={<PrivateRoute viewerAllowed><StartHostingPage /></PrivateRoute>} />
      {/* Viewers edit their profile here. */}
      <Route path="/settings" element={<PrivateRoute viewerAllowed><SettingsPage /></PrivateRoute>} />
      <Route path="/feedback" element={<PrivateRoute><FeedbackPage /></PrivateRoute>} />
      <Route path="/feedback/:ticketId" element={<PrivateRoute><FeedbackDetailPage /></PrivateRoute>} />
      <Route path="/organizations" element={<AdminGuard requirePlatform><Navigate to="/admin/organizations" replace /></AdminGuard>} />
      <Route path="/streams" element={<PrivateRoute><StreamsMonitorPage /></PrivateRoute>} />
      <Route path="/admin" element={<AdminGuard><AdminHomePage /></AdminGuard>} />
      <Route path="/admin/srt-bridges" element={<AdminGuard requirePlatform><AdminSrtBridgesPage /></AdminGuard>} />
      <Route path="/admin/teams" element={<AdminGuard><AdminTeamsPage /></AdminGuard>} />
      <Route path="/admin/users" element={<AdminGuard><AdminUsersPage /></AdminGuard>} />
      <Route path="/admin/organizations" element={<AdminGuard requirePlatform><AdminOrganizationsPage /></AdminGuard>} />
      <Route path="/admin/organizations/:organizationId" element={<AdminGuard requirePlatform><AdminOrganizationDetailPage /></AdminGuard>} />
      <Route path="/admin/private-beta" element={<AdminGuard requirePlatform><AdminPrivateBetaPage /></AdminGuard>} />
      {/* Hosts go to their events; viewers go home, not to start hosting, from an address that doesn't exist. */}
      <Route path="*" element={isAuthenticated && !authReady ? null : <Navigate to={host ? '/app' : '/'} replace />} />
    </Routes>
  )

  return (
    <div className="min-h-screen bg-transparent text-foreground transition-colors">
      <ScrollToTopOnRouteChange />
      {!isPlaySurface && (
        <div className="sticky top-0 z-50 border-b border-neutral-4 bg-neutral-1">
          <SiteHeader />
        </div>
      )}
      {/* A Play surface lays out its own landmarks: one main, with its header (banner) and footer (contentinfo) outside
          it. The app's other pages (Feedback, Admin, the streams monitor, the AI Producer) get its header, main and
          the one footer, SiteFooter. */}
      {isPlaySurface ? routes : <main>{routes}</main>}
      {feedback && !isPlaySurface ? <FeedbackFloatingButton /> : null}
      {!isPlaySurface && <SiteFooter />}
      <Toaster />
    </div>
  )
}
